import { type Graph, type GraphEdge } from '../models/graph';

export interface FlowStep {
  readonly edgeId: string;
  readonly backward: boolean;
}

export interface FlowRoute {
  readonly head: string;
  readonly steps: readonly FlowStep[];
}

export type RouteRejection = 'noSource' | 'notAdjacent' | 'saturated' | 'wrongWay' | 'revisit';

export type RouteExtension =
  | { readonly ok: true; readonly route: FlowRoute; readonly steppedBack: boolean }
  | { readonly ok: false; readonly reason: RouteRejection };

export function sourceId(graph: Graph): string | null {
  return graph.nodes.find((n) => n.type === 'source')?.id ?? null;
}

export function sinkId(graph: Graph): string | null {
  return graph.nodes.find((n) => n.type === 'sink')?.id ?? null;
}

export function startRoute(graph: Graph): FlowRoute {
  return { head: sourceId(graph) ?? '', steps: [] };
}

export function residual(edge: GraphEdge, flows: ReadonlyMap<string, number>): number {
  return Math.trunc(edge.capacity) - (flows.get(edge.id) ?? 0);
}

export function routeNodes(graph: Graph, route: FlowRoute): string[] {
  const nodes = [sourceId(graph) ?? ''];
  for (const step of route.steps) {
    const edge = graph.edges.find((e) => e.id === step.edgeId);
    if (!edge) continue;
    nodes.push(step.backward ? edge.sourceId : edge.targetId);
  }
  return nodes;
}

export function extendRoute(
  graph: Graph,
  flows: ReadonlyMap<string, number>,
  route: FlowRoute,
  edgeId: string,
): RouteExtension {
  if (!route.head) return { ok: false, reason: 'noSource' };

  const last = route.steps[route.steps.length - 1];
  if (last && last.edgeId === edgeId) {
    const steps = route.steps.slice(0, -1);
    const nodes = routeNodes(graph, { head: '', steps });
    return { ok: true, route: { head: nodes[nodes.length - 1] ?? '', steps }, steppedBack: true };
  }

  const edge = graph.edges.find((e) => e.id === edgeId);
  if (!edge) return { ok: false, reason: 'notAdjacent' };

  let next: string;
  let backward: boolean;
  if (edge.sourceId === route.head) {
    if (residual(edge, flows) <= 0) return { ok: false, reason: 'saturated' };
    next = edge.targetId;
    backward = false;
  } else if (edge.targetId === route.head) {
    if ((flows.get(edge.id) ?? 0) <= 0) return { ok: false, reason: 'wrongWay' };
    next = edge.sourceId;
    backward = true;
  } else {
    return { ok: false, reason: 'notAdjacent' };
  }

  if (routeNodes(graph, route).includes(next)) return { ok: false, reason: 'revisit' };

  return {
    ok: true,
    route: { head: next, steps: [...route.steps, { edgeId, backward }] },
    steppedBack: false,
  };
}

export function reachesSink(graph: Graph, route: FlowRoute): boolean {
  return route.steps.length > 0 && route.head === sinkId(graph);
}

export function bottleneck(
  graph: Graph,
  flows: ReadonlyMap<string, number>,
  route: FlowRoute,
): number {
  let limit = Number.POSITIVE_INFINITY;
  for (const step of route.steps) {
    const edge = graph.edges.find((e) => e.id === step.edgeId);
    if (!edge) return 0;
    const room = step.backward ? (flows.get(edge.id) ?? 0) : residual(edge, flows);
    limit = Math.min(limit, room);
  }
  return Number.isFinite(limit) ? Math.max(0, limit) : 0;
}

export function pushRoute(
  graph: Graph,
  flows: ReadonlyMap<string, number>,
  route: FlowRoute,
): Map<string, number> {
  const amount = bottleneck(graph, flows, route);
  const next = new Map(flows);
  for (const step of route.steps) {
    const current = next.get(step.edgeId) ?? 0;
    next.set(step.edgeId, step.backward ? current - amount : current + amount);
  }
  return next;
}
